import React, { useState, useEffect, useRef } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import api from '../api/api'
import { getVendorMenus, assignMenuToSections, getVendorSections } from '../api/api'
import CardLayoutTheme from '../theme/CardLayoutTheme'
import { useEnsureBottomNavVisibility } from '../hooks/useEnsureBottomNavVisibility'
import { useConfirm } from '../context/ConfirmContext'
import { useTheme } from '../context/ThemeContext'
import type { MenuCategory, MenuItem } from '../types/menu'
import { 
  PencilSquareIcon, 
  TrashIcon, 
  Squares2X2Icon, 
  Bars4Icon, 
  PlusIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon 
} from '@heroicons/react/24/outline'
import Modal from '../components/Modal'
import CategoryListItem from '../components/CategoryListItem'
import AddItemForm from '../components/AddItemForm'
import { CURRENCY } from '../config/currency'
import EditItemForm from '../components/EditItemForm'
import MenuItemPortionsManager from '../components/MenuItemPortionsManager'
import { 
  Button,
  Select,
  MenuItem as MuiMenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  FormGroup,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Popover,
  Drawer,
  Divider
} from '@mui/material'
import { useMediaQuery, useTheme as useMuiTheme } from '@mui/material'
import AppIconButton from '../components/ui/AppIconButton'
import LoadingScreen from '../components/LoadingScreen'
import {
  MenuBook as MenuBookIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  RestaurantMenu as RestaurantMenuIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Close as CloseIcon,
  ShoppingBagOutlined as TakeawayIcon
} from '@mui/icons-material'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import '../styles/ipad-responsive-fixes.css'
import { DietaryDot } from '../components/DietaryDot'

interface Menu {
  id: number;
  name: string;
  description?: string;
  menuType: string;
  isActive: boolean;
  isDefault: boolean;
  itemCount?: number;
  availableFrom?: string;
  availableTo?: string;
  availableDays?: string[] | string; // Can be array or comma-separated string from backend
  assignedSections?: Array<{id: number; name: string; floor: string}>;
}

interface Section {
  id: number;
  name: string;
  floorName?: string;
  floorId?: number;
  floorNumber?: number;
}

// Special section ID for Takeaway menu assignment
const TAKEAWAY_SECTION_ID = -1;

const WEEKDAYS = [
  { value: 'MON', label: 'Monday' },
  { value: 'TUE', label: 'Tuesday' },
  { value: 'WED', label: 'Wednesday' },
  { value: 'THU', label: 'Thursday' },
  { value: 'FRI', label: 'Friday' },
  { value: 'SAT', label: 'Saturday' },
  { value: 'SUN', label: 'Sunday' }
];

// ============================================
// Item Keyword Styles for Modern Dashboard Theme
// ============================================
const itemKeywordStyles: Array<{ keywords: string[]; emoji: string; color: string; gradient: string }> = [
  // Proteins - Muted/toned down colors
  { keywords: ['chicken', 'murgh', 'murg'], emoji: '🍗', color: '#9A7B4F', gradient: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)' },
  { keywords: ['mutton', 'lamb', 'gosht', 'keema'], emoji: '🥩', color: '#8B6B61', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)' },
  { keywords: ['fish', 'machli', 'pomfret', 'salmon'], emoji: '🐟', color: '#5B8A9A', gradient: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)' },
  { keywords: ['prawn', 'shrimp', 'jhinga'], emoji: '🦐', color: '#9B6B6B', gradient: 'linear-gradient(135deg, #FFF1F2 0%, #FFE4E6 100%)' },
  { keywords: ['egg', 'anda', 'omelette', 'omelet'], emoji: '🥚', color: '#8B7355', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF9C3 100%)' },
  { keywords: ['paneer', 'cottage cheese'], emoji: '🧀', color: '#9A7B4F', gradient: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)' },
  // Rice
  { keywords: ['biryani', 'pulao', 'pilaf', 'fried rice'], emoji: '🍚', color: '#9A7B4F', gradient: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)' },
  { keywords: ['rice', 'chawal'], emoji: '🍚', color: '#8B7355', gradient: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)' },
  // Noodles & Pasta
  { keywords: ['noodle', 'chowmein', 'hakka'], emoji: '🍜', color: '#9A7B4F', gradient: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)' },
  { keywords: ['pasta', 'spaghetti', 'penne'], emoji: '🍝', color: '#8B6B61', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)' },
  // Pizza & Burger
  { keywords: ['pizza'], emoji: '🍕', color: '#8B6B61', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)' },
  { keywords: ['burger'], emoji: '🍔', color: '#9A7B4F', gradient: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)' },
  { keywords: ['sandwich'], emoji: '🥪', color: '#6B8B5A', gradient: 'linear-gradient(135deg, #F7FEE7 0%, #ECFCCB 100%)' },
  { keywords: ['wrap', 'roll', 'kathi'], emoji: '🌯', color: '#5A8B6B', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
  // Indian
  { keywords: ['naan', 'kulcha', 'roti', 'paratha', 'chapati'], emoji: '🫓', color: '#8B7355', gradient: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)' },
  { keywords: ['curry', 'masala', 'gravy', 'korma', 'tikka'], emoji: '🍛', color: '#9A7B4F', gradient: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)' },
  { keywords: ['dal', 'daal', 'lentil'], emoji: '🥘', color: '#8B7355', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF9C3 100%)' },
  { keywords: ['samosa'], emoji: '🥟', color: '#9A7B4F', gradient: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)' },
  { keywords: ['tandoor', 'kebab', 'seekh'], emoji: '🔥', color: '#8B6B61', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)' },
  // South Indian
  { keywords: ['dosa', 'dosai'], emoji: '🥞', color: '#8B7355', gradient: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)' },
  { keywords: ['idli', 'idly'], emoji: '⚪', color: '#6B7280', gradient: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)' },
  // Chinese
  { keywords: ['momo', 'dumpling'], emoji: '🥟', color: '#9A7B4F', gradient: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)' },
  { keywords: ['manchurian'], emoji: '🥦', color: '#5A8B6B', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
  // Soups & Salads
  { keywords: ['soup'], emoji: '🍜', color: '#9A7B4F', gradient: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)' },
  { keywords: ['salad'], emoji: '🥗', color: '#5A8B6B', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
  // Desserts
  { keywords: ['ice cream', 'gelato', 'sundae'], emoji: '🍨', color: '#9B6B8B', gradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' },
  { keywords: ['cake', 'pastry', 'brownie'], emoji: '🍰', color: '#8B5A7B', gradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' },
  { keywords: ['gulab jamun', 'rasgulla', 'ladoo', 'jalebi'], emoji: '🍬', color: '#7B5A8B', gradient: 'linear-gradient(135deg, #FDF4FF 0%, #FAE8FF 100%)' },
  // Drinks
  { keywords: ['lassi', 'buttermilk'], emoji: '🥛', color: '#5B8A9A', gradient: 'linear-gradient(135deg, #ECFEFF 0%, #CFFAFE 100%)' },
  { keywords: ['juice', 'nimbu', 'lemonade'], emoji: '🧃', color: '#9A7B4F', gradient: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)' },
  { keywords: ['shake', 'smoothie', 'frappe'], emoji: '🥤', color: '#9B6B8B', gradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' },
  { keywords: ['coffee', 'cappuccino', 'latte'], emoji: '☕', color: '#8B7355', gradient: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)' },
  { keywords: ['tea', 'chai'], emoji: '🍵', color: '#5A8B6B', gradient: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' },
  { keywords: ['cold drink', 'soda', 'coca', 'pepsi', 'coke'], emoji: '🥤', color: '#5B8A9A', gradient: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)' },
  // Snacks
  { keywords: ['fries', 'french fries', 'wedges'], emoji: '🍟', color: '#8B7355', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF9C3 100%)' },
  { keywords: ['popcorn'], emoji: '🍿', color: '#9A7B4F', gradient: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)' },
  // Default
  { keywords: ['default'], emoji: '🍽️', color: '#6B7280', gradient: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)' },
];

const getItemStyle = (itemName: string) => {
  const normalizedName = itemName.toLowerCase();
  for (const mapping of itemKeywordStyles) {
    for (const keyword of mapping.keywords) {
      if (normalizedName.includes(keyword)) {
        return { emoji: mapping.emoji, color: mapping.color, gradient: mapping.gradient };
      }
    }
  }
  // Default style
  return { emoji: '🍽️', color: '#6B7280', gradient: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)' };
};
// ============================================

const AdminVendorMenu: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>()
  const location = useLocation();
  const navigate = useNavigate();
  const confirm = useConfirm()
  const { theme, currentThemeName } = useTheme();
  const isModernTheme = currentThemeName === 'modernDashboard';
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [menus, setMenus] = useState<Menu[]>([])
  const [selectedMenuId, setSelectedMenuId] = useState<number | null>(location.state?.menuId || null)
  const [categories, setCategories] = useState<MenuCategory[]>([])
  const [refreshTrigger, setRefreshTrigger] = useState(0) // Add refresh trigger
  const [sections, setSections] = useState<Section[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingMenus, setLoadingMenus] = useState<boolean>(true)
  const hasLoadedMenusOnce = useRef(false)
  const [error, setError] = useState<string | null>(null)
  // GB allergen completeness nudge (UK plan Phase 4); null = hidden
  const [allergenNudge, setAllergenNudge] = useState<{ count: number } | null>(null)

  useEffect(() => {
    // Only nudge GB vendors (country config is vendor-scoped; admins viewing a
    // vendor's menu simply get no nudge). Failures are silently ignored.
    (async () => {
      try {
        const country = await api.get('/api/vendor/config/country')
        if (country.data?.taxLabel !== 'VAT') return
        const completeness = await api.get('/api/menu-management/allergen-completeness')
        if (completeness.data?.undeclaredCount > 0) {
          setAllergenNudge({ count: completeness.data.undeclaredCount })
        }
      } catch {
        // not a vendor session or endpoint unavailable — no nudge
      }
    })()
  }, [])
  const [createMenuDialogOpen, setCreateMenuDialogOpen] = useState(false)
  const [configDialogOpen, setConfigDialogOpen] = useState(false)
  const [assignSectionsDialogOpen, setAssignSectionsDialogOpen] = useState(false)
  const [selectedSections, setSelectedSections] = useState<number[]>([])
  const [sectionsToReassign, setSectionsToReassign] = useState<{sectionId: number; currentMenuName: string}[]>([])
  const [menuConfig, setMenuConfig] = useState<Partial<Menu>>({})
  const [newMenuForm, setNewMenuForm] = useState({
    name: '',
    description: '',
    menuType: 'REGULAR',
    isActive: true,
    isDefault: false
  })
  
  // Ensure the bottom navigation is always visible
  useEnsureBottomNavVisibility();
  
  // Active state variables (keeping existing functionality)
  const [editingCategoryForms, setEditingCategoryForms] = useState<Record<number, { name: string; description: string }>>({})
  const [editingItemForms, setEditingItemForms] = useState<Record<number, { name: string; price: string; available: boolean; quantity: string; description: string; kitchenId?: string; spiceLevel?: string; allowsSpiceCustomization?: boolean; imageKey?: string; imageUrl?: string; dietaryType?: string }>>({})
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')
  // Dietary filter for the admin item list. VEG includes VEGAN (a vegan dish
  // is vegetarian); VEGAN is the strict subset.
  const [dietaryFilter, setDietaryFilter] = useState<'ALL' | 'VEG' | 'NON_VEG' | 'VEGAN'>('ALL')
  const matchesDietary = (item: MenuItem) => {
    if (dietaryFilter === 'ALL') return true
    if (dietaryFilter === 'VEG') return item.dietaryType === 'VEG' || item.dietaryType === 'VEGAN'
    return item.dietaryType === dietaryFilter
  }
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false)
  const [currentCategoryId, setCurrentCategoryId] = useState<number | null>(null)
  
  // New state variables for edit item modal
  const [isEditItemModalOpen, setIsEditItemModalOpen] = useState(false)
  const [editModalData, setEditModalData] = useState<{categoryId: number; itemId: number} | null>(null)
  
  // State for portions management
  const [portionsModalData, setPortionsModalData] = useState<{
    menuItemId: number;
    menuItemName: string;
    menuItemPrice: number;
  } | null>(null)
  
  // State for expandable items
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())
  
  // State for menu dropdown
  const [isMenuDropdownOpen, setIsMenuDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  // State for inline topping input
  const [inlineToppingInputs, setInlineToppingInputs] = useState<Record<number, string>>({})
  // Kitchens for inline assignment
  const [kitchens, setKitchens] = useState<Array<{ id: number; name: string; isDefault?: boolean; kitchenType?: string }>>([])
  const [updatingKitchenIds, setUpdatingKitchenIds] = useState<Set<number>>(new Set())
  const [pendingKitchenByItemId, setPendingKitchenByItemId] = useState<Record<number, number | undefined>>({})
  const [kitchenLabelWidthByItemId, setKitchenLabelWidthByItemId] = useState<Record<number, number>>({})

  // Portions inline display state (frontend-only)
  type Portion = { id: number; portionName: string; priceModifier: number; isDefault: boolean; displayOrder: number }
  const [portionsByItemId, setPortionsByItemId] = useState<Record<number, Portion[]>>({})
  const [loadingPortionsForIds, setLoadingPortionsForIds] = useState<Set<number>>(new Set())
  const [expandedPortionsItemId, setExpandedPortionsItemId] = useState<number | null>(null)
  const [portionsAnchorEl, setPortionsAnchorEl] = useState<HTMLElement | null>(null)

  // State for expanded sections view
  const [expandedSectionsAnchorEl, setExpandedSectionsAnchorEl] = useState<HTMLElement | null>(null)

  // Click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsMenuDropdownOpen(false)
      }
    }

    if (isMenuDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuDropdownOpen])

  // Fetch available menus
  useEffect(() => {
    const loadInitialData = async () => {
      // Fetch sections and kitchens first
      const [sectionsData] = await Promise.all([fetchSections(), fetchKitchens()])
      // Then fetch menus with sections data
      await fetchMenus(sectionsData)
    }
    loadInitialData()
  }, [])

  const fetchKitchens = async () => {
    try {
      const res = await api.get('/api/kitchens')
      setKitchens(Array.isArray(res.data) ? res.data : [])
    } catch (err) {
      console.error('Failed to fetch kitchens:', err)
      setKitchens([])
    }
  }

  // Lazy fetch portions for item and cache
  const fetchPortionsForItem = async (itemId: number) => {
    if (loadingPortionsForIds.has(itemId) || portionsByItemId[itemId]) return
    setLoadingPortionsForIds(prev => new Set(prev).add(itemId))
    try {
      const res = await api.get(`/api/menu-items/${itemId}/portions`)
      let list: Portion[] = Array.isArray(res.data) ? res.data : []
      const firstDefault = list.findIndex(p => p.isDefault)
      if (firstDefault !== -1) {
        list = list.map((p, idx) => ({
          ...p,
          isDefault: idx === firstDefault,
          priceModifier: idx === firstDefault ? 0 : (p.priceModifier || 0),
          displayOrder: (p as any).displayOrder ?? idx + 1,
        }))
      } else {
        list = list.map((p, idx) => ({
          ...p,
          isDefault: idx === 0,
          priceModifier: idx === 0 ? 0 : (p.priceModifier || 0),
          displayOrder: (p as any).displayOrder ?? idx + 1,
        }))
      }
      list.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      setPortionsByItemId(prev => ({ ...prev, [itemId]: list }))
    } catch (e) {
      console.error('Failed to fetch portions for item', itemId, e)
      setPortionsByItemId(prev => ({ ...prev, [itemId]: [] }))
    } finally {
      setLoadingPortionsForIds(prev => {
        const next = new Set(prev)
        next.delete(itemId)
        return next
      })
    }
  }

  const fetchMenus = async (sectionsData?: Section[]) => {
    try {
      setLoadingMenus(true)
      const menuData = await getVendorMenus()

      // Use passed sections data or current state
      const availableSections = sectionsData || sections

      // Fetch assigned sections for each menu
      const menusWithSections = await Promise.all(
        menuData.map(async (menu: Menu) => {
          try {
            const response = await api.get(`/api/menu-management/menus/${menu.id}/sections`)
            const assignedSectionIds = response.data || []

            // Fetch section details if we have sections available
            const enrichedSections = assignedSectionIds.map((id: number) => {
              const sectionDetail = availableSections.find(s => s.id === id)
              return {
                id,
                name: sectionDetail?.name || '',
                floor: sectionDetail?.floorName || ''
              }
            })

            return {
              ...menu,
              assignedSections: enrichedSections
            }
          } catch (err) {
            console.error(`Failed to fetch sections for menu ${menu.name}:`, err)
            return { ...menu, assignedSections: [] }
          }
        })
      )

      setMenus(menusWithSections)

      // If no menu is selected and we have menus, select the default or first one
      if (!selectedMenuId && menusWithSections.length > 0) {
        const defaultMenu = menusWithSections.find((m: Menu) => m.isDefault)
        setSelectedMenuId(defaultMenu?.id || menusWithSections[0].id)
      }
    } catch (err) {
      console.error('Failed to fetch menus:', err)
      setError('Failed to load menus')
    } finally {
      setLoadingMenus(false)
      hasLoadedMenusOnce.current = true
    }
  }

  const fetchSections = async () => {
    try {
      const sectionData = await getVendorSections()
      // Ensure section IDs are numbers and dedupe by name+floor
      const sectionsWithNumericIds: Section[] = sectionData.map((section: Section) => ({
        ...section,
        id: Number(section.id)
      }))
      const keyToSection: Record<string, Section> = {}
      for (const s of sectionsWithNumericIds) {
        const key = `${String(s.name || '').trim().toLowerCase()}|${String(s.floorName || '').trim().toLowerCase()}`
        if (!keyToSection[key]) keyToSection[key] = s
      }
      const dedupedSections = Object.values(keyToSection)
      setSections(dedupedSections)
      return dedupedSections // Return the sections for use in fetchMenus
    } catch (err) {
      console.error('Failed to fetch sections:', err)
      return []
    }
  }

  // Fetch categories for selected menu
  useEffect(() => {
    if (!vendorId || !selectedMenuId) return
    
    setLoading(true)
    console.log(`Fetching categories for menu ${selectedMenuId}`)
    // Use the menu-specific endpoint to get categories with their items
    api.get(`/api/menu-management/menus/${selectedMenuId}/categories`)
      .then(res => {
        // Check if the response is an error object
        if (res.data.error) {
          console.error('Error from API:', res.data.error);
          setError(res.data.error);
          setCategories([]);
          return;
        }
        
        // Ensure res.data is an array
        if (!Array.isArray(res.data)) {
          console.error('Unexpected response format:', res.data);
          setError('Invalid response format from server');
          setCategories([]);
          return;
        }
        
        console.log(`Received ${res.data.length} categories for menu ${selectedMenuId}`, res.data)
        // Transform the response to match our expected format
        const categoriesWithItems = res.data.map((categoryData: MenuCategory) => {
          const processedItems = (categoryData.items || []).map((it: any) => {
            const resolvedKitchenId = (it.kitchen && it.kitchen.id) ?? it.kitchenId;
            const resolvedKitchen = resolvedKitchenId ? kitchens.find(k => Number(k.id) === Number(resolvedKitchenId)) : null;
            return {
              ...it,
              kitchenId: resolvedKitchenId !== undefined ? Number(resolvedKitchenId) : undefined,
              kitchen: resolvedKitchen || (it.kitchen ?? null),
            };
          })
          // Oldest-first sorting for items without backend change
          .sort((a: any, b: any) => {
            const aOrder = (a.displayOrder ?? a.order ?? a.id) as number;
            const bOrder = (b.displayOrder ?? b.order ?? b.id) as number;
            return aOrder - bOrder;
          });

          return {
            id: categoryData.id,
            name: categoryData.name,
            description: categoryData.description,
            items: processedItems,
          };
        });
        // Sort categories so older (first added) appear first without backend changes
        const sortedCategories = [...categoriesWithItems].sort((a: any, b: any) => {
          const aOrder = (a.displayOrder ?? a.order ?? a.id) as number;
          const bOrder = (b.displayOrder ?? b.order ?? b.id) as number;
          return aOrder - bOrder;
        });
        
        setCategories(sortedCategories);

        // Extract portions from the API response (already included) instead of making separate calls
        const allItems: any[] = sortedCategories.flatMap((cat: any) => cat.items || [])
        const portionsMap: Record<number, Portion[]> = {}
        allItems.forEach(it => {
          if (typeof it?.id === 'number' && Array.isArray(it.portions)) {
            // Process portions from the API response
            const list: Portion[] = it.portions.map((p: any) => ({
              id: p.id,
              portionName: p.portionName || p.name || '',
              priceModifier: p.priceModifier ?? 0,
              isDefault: p.isDefault ?? false,
              displayOrder: p.displayOrder ?? 0,
            }))
            // Sort by display order
            list.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
            // Ensure at least one default
            if (list.length > 0 && !list.some(p => p.isDefault)) {
              list[0].isDefault = true
            }
            portionsMap[it.id] = list
          }
        })
        setPortionsByItemId(prev => ({ ...prev, ...portionsMap }))
      })
      .catch(err => {
        console.error('API call failed:', err);
        setError(err.response?.data?.error || err.message || 'Failed to load categories');
        setCategories([]);
      })
      .finally(() => setLoading(false))
  }, [vendorId, selectedMenuId, refreshTrigger, kitchens.length]) // include kitchens for enrichment

  const handleMenuChange = (menuId: number) => {
    setSelectedMenuId(menuId)
  }

  const handleCreateMenu = async () => {
    try {
      const response = await api.post('/api/menu-management/menus', newMenuForm)
      await fetchMenus()
      setSelectedMenuId(response.data.id)
      setCreateMenuDialogOpen(false)
      setNewMenuForm({
        name: '',
        description: '',
        menuType: 'REGULAR',
        isActive: true,
        isDefault: false
      })
    } catch (err) {
      console.error('Failed to create menu:', err)
      setError('Failed to create menu')
    }
  }

  const handleOpenConfig = () => {
    const currentMenu = menus.find(m => m.id === selectedMenuId)
    if (currentMenu) {
      // Parse availableDays: if it's a string, convert to array; if it's already an array, use as-is
      let parsedDays: string[] = [];
      if (currentMenu.availableDays) {
        if (typeof currentMenu.availableDays === 'string') {
          // Backend returns comma-separated string, split it into array
          parsedDays = currentMenu.availableDays.split(',').map(d => d.trim()).filter(d => d);
        } else if (Array.isArray(currentMenu.availableDays)) {
          // Already an array, use as-is
          parsedDays = currentMenu.availableDays;
        }
      }
      
      setMenuConfig({
        availableFrom: currentMenu.availableFrom,
        availableTo: currentMenu.availableTo,
        availableDays: parsedDays
      })
      setConfigDialogOpen(true)
    }
  }

  const handleSaveConfig = async () => {
    if (!selectedMenuId) return
    
    try {
      await api.put(`/api/menu-management/menus/${selectedMenuId}`, menuConfig)
      await fetchMenus()
      setConfigDialogOpen(false)
    } catch (err) {
      console.error('Failed to save config:', err)
      setError('Failed to save configuration')
    }
  }

  const handleOpenAssignSections = async () => {
    const currentMenu = menus.find(m => m.id === selectedMenuId)
    if (currentMenu) {
      // Clear previous selections first
      setSelectedSections([])

      try {
        // Fetch fresh section assignments from API
        const response = await api.get(`/api/menu-management/menus/${selectedMenuId}/sections`)
        const assignedSectionIds = response.data || []

        // Ensure IDs are numbers for comparison
        const numericIds = Array.isArray(assignedSectionIds)
          ? assignedSectionIds.map(id => Number(id)).filter(id => !isNaN(id))
          : []

        console.log('Fresh fetch - assigned sections for menu', currentMenu.name, ':', numericIds)
        setSelectedSections(numericIds)

        // Small delay to ensure state is updated
        setTimeout(() => {
          setAssignSectionsDialogOpen(true)
        }, 50)
      } catch (err) {
        console.error('Failed to fetch assigned sections:', err)
        // Use the assignedSections from the menu state as fallback
        const assignedIds = currentMenu.assignedSections?.map(s => Number(s.id)) || []
        console.log('Fallback - using cached assigned sections:', assignedIds)
        setSelectedSections(assignedIds)
        setAssignSectionsDialogOpen(true)
      }
    }
  }

  const handleSectionToggle = async (sectionId: number, checked: boolean) => {
    const assignment = getSectionAssignment(sectionId);
    
    if (checked) {
      // Adding section
      if (assignment && assignment.menuId !== selectedMenuId) {
        // Section is assigned to different menu - need confirmation
        const result = await confirm({
          title: 'Reassign Section',
          description: `Section "${sections.find(s => s.id === sectionId)?.name}" is currently assigned to "${assignment.menuName}". Do you want to reassign it to "${selectedMenu?.name}"?`,
          confirmText: 'Reassign',
          cancelText: 'Cancel'
        });
        
        if (!result) return; // User cancelled
        
        // Add to reassignment list
        setSectionsToReassign(prev => [
          ...prev.filter(s => s.sectionId !== sectionId),
          { sectionId, currentMenuName: assignment.menuName }
        ]);
      }
      setSelectedSections(prev => [...prev, sectionId]);
    } else {
      // Removing section
      setSelectedSections(prev => prev.filter(id => id !== sectionId));
      setSectionsToReassign(prev => prev.filter(s => s.sectionId !== sectionId));
    }
  }

  const handleAssignSections = async () => {
    if (!selectedMenuId) return

    try {
      // Ensure section IDs are numbers before sending to API
      const numericSectionIds = selectedSections.map(id => Number(id))
      await assignMenuToSections(selectedMenuId, numericSectionIds)

      // Update the local menus state to reflect the new assignments
      setMenus(prevMenus =>
        prevMenus.map(menu =>
          menu.id === selectedMenuId
            ? {
                ...menu,
                assignedSections: numericSectionIds.map(id => {
                  const sectionDetail = sections.find(s => s.id === id)
                  return {
                    id,
                    name: sectionDetail?.name || '',
                    floor: sectionDetail?.floorName || ''
                  }
                })
              }
            : menu
        )
      )

      // Optionally refetch to ensure consistency
      await fetchMenus()

      setAssignSectionsDialogOpen(false)
      setSelectedSections([]) // Clear selected sections after save
      setSectionsToReassign([]) // Clear reassignment tracking
    } catch (err) {
      console.error('Failed to assign sections:', err)
      setError('Failed to assign sections')
    }
  }

  const navigateToMenuManagement = () => {
    navigate('/vendor/multi-menu')
  }

  // Rest of the existing methods remain the same...
  const handleDeleteCategory = async (categoryId: number) => {
    if (!vendorId || !selectedMenuId) return;
    
    const result = await confirm({
      title: 'Delete Category',
      description: 'Are you sure you want to delete this category and all its items?',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    
    if (!result) return;
    
    try {
      await api.delete(`/vendors/${vendorId}/menus/${selectedMenuId}/categories/${categoryId}`);
      setCategories(prev => prev.filter(cat => cat.id !== categoryId));
    } catch (error) {
      console.error('Failed to delete category:', error);
    }
  };

  const getKitchenColorClasses = (kitchenType: string) => {
    switch (kitchenType?.toUpperCase()) {
      case 'CHINESE':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'INDIAN':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'ITALIAN':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'DESSERTS':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300';
      case 'BEVERAGES':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'GENERAL':
      default:
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
    }
  }

  const groupSectionsByFloor = () => {
    const grouped: Record<string, Section[]> = {};
    sections.forEach(section => {
      const floorName = section.floorName || 'Unassigned';
      if (!grouped[floorName]) {
        grouped[floorName] = [];
      }
      grouped[floorName].push(section);
    });
    
    // Sort floors by floor number
    const sortedGrouped: Record<string, Section[]> = {};
    Object.entries(grouped).sort((a, b) => {
      const floorA = a[1][0]?.floorNumber ?? 999;
      const floorB = b[1][0]?.floorNumber ?? 999;
      return floorA - floorB;
    }).forEach(([key, value]) => {
      sortedGrouped[key] = value;
    });
    
    return sortedGrouped;
  }

  const getSectionAssignment = (sectionId: number) => {
    // Find which menu this section is currently assigned to
    for (const menu of menus) {
      if (menu.assignedSections?.some(s => s.id === sectionId)) {
        return {
          menuId: menu.id,
          menuName: menu.name,
          isDefault: menu.isDefault
        };
      }
    }
    return null; // Unassigned (goes to default)
  }

  const isSectionAvailable = (sectionId: number, currentMenuId?: number) => {
    const assignment = getSectionAssignment(sectionId);
    if (!assignment) return true; // Unassigned sections are available
    if (assignment.menuId === currentMenuId) return true; // Already assigned to current menu
    return false; // Assigned to different menu
  }

  const handleDeleteItem = async (categoryId: number, itemId: number) => {
    if (!vendorId || !selectedMenuId) return;
    
    const result = await confirm({
      title: 'Delete Item',
      description: 'Are you sure you want to delete this item?',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    
    if (!result) return;
    
    try {
      const response = await api.delete(`/vendors/${vendorId}/menus/${selectedMenuId}/categories/${categoryId}/items/${itemId}`);
      
      // Check if it was a 204 No Content (successful delete)
      if (response.status === 204) {
        // Actually deleted, remove from list
        setCategories(prev => prev.map(cat => 
          cat.id === categoryId
            ? { ...cat, items: cat.items.filter(item => item.id !== itemId) }
            : cat
        ));
      } else if (response.data?.message) {
        // Item was marked as unavailable instead of deleted
        setCategories(prev => prev.map(cat => 
          cat.id === categoryId
            ? { 
                ...cat, 
                items: cat.items.map(item => 
                  item.id === itemId 
                    ? { ...item, available: false, isAvailable: false }
                    : item
                )
              }
            : cat
        ));
        
        // Show info message
        setError(response.data.message);
        setTimeout(() => setError(null), 5000);
      }
    } catch (error) {
      console.error('Failed to delete item:', error);
      const err = error as { response?: { data?: { error?: string; message?: string } } };
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to delete item');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleAddItem = (categoryId: number) => {
    setCurrentCategoryId(categoryId);
    setIsAddItemModalOpen(true);
  };

  interface ItemFormData {
    name: string;
    description?: string;
    price: number;
    available: boolean;
    quantity?: number | null;
    kitchenId?: number;
    imageKey?: string;
  }

  const handleAddItemSubmit = async (itemData: ItemFormData) => {
    if (!vendorId || !currentCategoryId || !selectedMenuId) return;
    
    try {
      // Include menu and kitchen in the item data (backend expects objects with id)
      const itemWithMenuId = {
        ...itemData,
        menu: { id: selectedMenuId },
        kitchen: itemData.kitchenId ? { id: itemData.kitchenId } : null
      };
      
      const response = await api.post(
        `/vendors/${vendorId}/menus/${selectedMenuId}/categories/${currentCategoryId}/items`,
        itemWithMenuId
      );
      
      // Normalize response so UI reflects selected kitchen immediately
      {
        const updated = response.data || {} as any;
        const normalized = { ...updated } as any;
        // Normalize availability fields
        const normalizedAvailability = (updated.isAvailable ?? updated.available ?? itemData.available);
        normalized.isAvailable = !!normalizedAvailability;
        normalized.available = !!normalizedAvailability;
        const resolvedKitchenId =
          (updated.kitchen && updated.kitchen.id) ?? updated.kitchenId ?? itemData.kitchenId ?? undefined;
        if (resolvedKitchenId !== undefined) {
          const resolvedKitchen = kitchens.find(k => Number(k.id) === Number(resolvedKitchenId));
          normalized.kitchenId = Number(resolvedKitchenId);
          if (resolvedKitchen) {
            normalized.kitchen = resolvedKitchen as any;
          }
        } else {
          normalized.kitchen = null;
          normalized.kitchenId = undefined;
        }
        setCategories(prev => prev.map(cat => 
          cat.id === currentCategoryId
            ? { ...cat, items: [...cat.items, normalized] }
            : cat
        ));
      }
      
      setIsAddItemModalOpen(false);
      setCurrentCategoryId(null);
    } catch (error) {
      console.error('Failed to add item:', error);
    }
  };

  const handleEditItemSubmit = async (itemData: ItemFormData) => {
    if (!vendorId || !editModalData || !selectedMenuId) return;
    
    const { categoryId, itemId } = editModalData;
    
    try {
      // Include menu and kitchen to ensure they're not lost during updates
      const itemWithMenuId = {
        ...itemData,
        menu: { id: selectedMenuId },
        kitchen: itemData.kitchenId ? { id: itemData.kitchenId } : null
      };
      
      const response = await api.put(
        `/vendors/${vendorId}/menus/${selectedMenuId}/categories/${categoryId}/items/${itemId}`,
        itemWithMenuId
      );
      
      // Normalize response so UI reflects selected kitchen immediately
      {
        const updated = response.data || {} as any;
        const normalized = { ...updated } as any;
        // Normalize availability fields
        const normalizedAvailability = (updated.isAvailable ?? updated.available ?? itemData.available);
        normalized.isAvailable = !!normalizedAvailability;
        normalized.available = !!normalizedAvailability;
        const resolvedKitchenId =
          (updated.kitchen && updated.kitchen.id) ?? updated.kitchenId ?? itemData.kitchenId ?? undefined;
        if (resolvedKitchenId !== undefined) {
          const resolvedKitchen = kitchens.find(k => Number(k.id) === Number(resolvedKitchenId));
          normalized.kitchenId = Number(resolvedKitchenId);
          if (resolvedKitchen) {
            normalized.kitchen = resolvedKitchen as any;
          }
        } else {
          normalized.kitchen = null;
          normalized.kitchenId = undefined;
        }

        setCategories(prev => prev.map(cat => 
          cat.id === categoryId
            ? { 
                ...cat, 
                items: cat.items.map(item => 
                  item.id === itemId ? normalized : item
                ) 
              }
            : cat
        ));
      }
      
      setIsEditItemModalOpen(false);
      setEditModalData(null);
    } catch (error) {
      console.error('Failed to update item:', error);
    }
  };

  const handleAddTopping = async (categoryId: number, itemId: number) => {
    if (!vendorId || !selectedMenuId || !inlineToppingInputs[itemId]?.trim()) return;
    
    try {
      const response = await api.post(
        `/vendors/${vendorId}/menus/${selectedMenuId}/categories/${categoryId}/items/${itemId}/toppings`,
        { name: inlineToppingInputs[itemId].trim() }
      );
      
      setCategories(prev => prev.map(cat => 
        cat.id === categoryId
          ? {
              ...cat,
              items: cat.items.map(item => 
                item.id === itemId
                  ? { ...item, toppings: [...(item.toppings || []), response.data] }
                  : item
              )
            }
          : cat
      ));
      
      setInlineToppingInputs(prev => ({ ...prev, [itemId]: '' }));
    } catch (error) {
      console.error('Failed to add topping:', error);
    }
  };

  // Inline kitchen update without opening the modal
  const updateItemKitchenInline = async (
    categoryId: number,
    item: { id: number; name: string; price: number; available: boolean; quantity?: number | null; description?: string },
    newKitchenId: number | undefined
  ) => {
    if (!vendorId || !selectedMenuId) return;
    setUpdatingKitchenIds(prev => new Set(prev).add(item.id));
    try {
      const itemWithMenuId = {
        id: item.id,
        name: item.name,
        price: item.price,
        available: item.isAvailable,
        quantity: item.quantity ?? null,
        description: item.description || '',
        menu: { id: selectedMenuId },
        kitchen: newKitchenId ? { id: newKitchenId } : null,
        kitchenId: newKitchenId ?? null,
      } as any;

      const response = await api.put(
        `/vendors/${vendorId}/menus/${selectedMenuId}/categories/${categoryId}/items/${item.id}`,
        itemWithMenuId
      );

      // Some responses may return only kitchenId; normalize so UI reflects immediately
      const updated = response.data || {};
      const normalized = { ...updated } as any;
      const resolvedKitchenId = 
        (updated.kitchen && updated.kitchen.id) ?? updated.kitchenId ?? newKitchenId ?? undefined;
      if (resolvedKitchenId !== undefined) {
        const resolvedKitchen = kitchens.find(k => k.id === Number(resolvedKitchenId));
        normalized.kitchenId = Number(resolvedKitchenId);
        if (resolvedKitchen) {
          normalized.kitchen = resolvedKitchen as any;
        }
      } else if (newKitchenId === undefined) {
        normalized.kitchen = null;
        normalized.kitchenId = undefined;
      }

      setCategories(prev => prev.map(cat =>
        cat.id === categoryId
          ? { ...cat, items: cat.items.map(it => (it.id === item.id ? normalized : it)) }
          : cat
      ));
      // Also force a refetch to fully sync with backend
      setRefreshTrigger(prev => prev + 1)
    } catch (err) {
      console.error('Failed to update item kitchen inline:', err);
    } finally {
      setUpdatingKitchenIds(prev => { const n = new Set(prev); n.delete(item.id); return n; });
    }
  };

  const handleDeleteTopping = async (categoryId: number, itemId: number, toppingId: number) => {
    if (!vendorId || !selectedMenuId) return;
    
    const result = await confirm({
      title: 'Delete Topping',
      description: 'Are you sure you want to delete this topping?',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    
    if (!result) return;
    
    try {
      await api.delete(
        `/vendors/${vendorId}/menus/${selectedMenuId}/categories/${categoryId}/items/${itemId}/toppings/${toppingId}`
      );
      
      setCategories(prev => prev.map(cat => 
        cat.id === categoryId
          ? {
              ...cat,
              items: cat.items.map(item => 
                item.id === itemId
                  ? { ...item, toppings: item.toppings?.filter(t => t.id !== toppingId) || [] }
                  : item
              )
            }
          : cat
      ));
    } catch (error) {
      console.error('Failed to delete topping:', error);
    }
  };

  const selectedMenu = menus.find(m => m.id === selectedMenuId)

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <>
        {loadingMenus && !hasLoadedMenusOnce.current ? (
          <LoadingScreen message="Loading menus..." inline />
        ) : error ? (
          <Box p={4}>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button
              variant="contained"
              startIcon={<ArrowLeftIcon />}
              onClick={() => navigate('/vendor/multi-menu')}
              sx={{ backgroundColor: theme.primary, color: 'var(--text-inverse)', '&:hover': { backgroundColor: theme.primaryHover || theme.primary } }}
            >
              Back to Menu Management
            </Button>
          </Box>
        ) : menus.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography variant="h6" color="text.secondary" mb={2}>
              No menus found. Create your first menu to get started.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateMenuDialogOpen(true)}
              sx={{
                backgroundColor: theme.primary,
                color: theme.button?.primary?.text || '#FFFFFF',
                '&:hover': {
                  backgroundColor: theme.pressed || theme.primary,
                }
              }}
            >
              Create First Menu
            </Button>
          </Box>
        ) : (
          <>
            {loading && (
              <Box py={6}>
                <LoadingScreen message="Loading menu..." />
              </Box>
            )}
            {/* GB allergen completeness nudge (UK plan Phase 4) — dismissible, GB vendors only */}
            {allergenNudge && (
              <Alert severity="warning" sx={{ m: 2 }} onClose={() => setAllergenNudge(null)}>
                {allergenNudge.count} menu item{allergenNudge.count === 1 ? ' has' : 's have'} no allergen
                declaration. UK food businesses must make allergen information available before purchase —
                edit each item to add its allergens.
              </Alert>
            )}
            <CardLayoutTheme
              title=''
              headerActions={
                <div className="w-full desktop-header-actions desktop-compact-spacing tablet-header-actions tablet-compact-spacing">
                  {/* Top Row - Menu Selector and Actions */}
                                      <div className="flex flex-col sm:flex-row gap-responsive-md items-stretch sm:items-start desktop-header-row tablet-header-row">
                    {/* Menu Dropdown */}
                                          <div className="relative w-full sm:w-auto sm:max-w-md desktop-menu-dropdown tablet-menu-dropdown" ref={dropdownRef}>
                      <button
                        onClick={() => {
                          console.log('Menu dropdown clicked, current state:', isMenuDropdownOpen);
                          console.log('Available menus:', menus);
                          setIsMenuDropdownOpen(!isMenuDropdownOpen);
                        }}
                        className={`flex items-center gap-responsive-sm px-3 py-2 rounded-lg transition-all duration-200 menu-dropdown-button ${
                          isMenuDropdownOpen ? 'shadow-sm' : ''
                        }`}
                        style={{
                          backgroundColor: isMenuDropdownOpen ? (theme.gray?.[50] || '#F9F9F9') : 'transparent',
                          border: 'none',
                        }}
                      >
                        <MenuBookIcon className="icon-sm flex-shrink-0" style={{ color: theme.primary }} />
                        <div className="flex-1 text-left min-w-0">
                          {selectedMenu ? (
                            <>
                              <div className="font-medium truncate" style={{ color: theme.text }}>
                                {selectedMenu.name}
                              </div>
                              <div className="text-xs flex items-center gap-2" style={{ color: theme.gray?.[600] || '#666666' }}>
                                <span>{selectedMenu.menuType}</span>
                                {selectedMenu.isDefault && (
                                  <span style={{ color: theme.primary }}>• Default</span>
                                )}
                              </div>
                            </>
                          ) : (
                            <div style={{ color: theme.gray?.[600] || '#666666' }}>Select a menu</div>
                          )}
                        </div>
                        <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isMenuDropdownOpen ? 'rotate-180' : ''}`}
                                         style={{ color: theme.gray?.[500] || '#888', marginLeft: 4 }} />
                      </button>
                      
                      {/* Dropdown Menu */}
                      {isMenuDropdownOpen && (
                        <div className="absolute top-full left-0 mt-2 rounded-lg shadow-xl max-h-[400px] overflow-y-auto min-w-[280px] max-w-md"
                             style={{
                               backgroundColor: theme.components?.card?.background || '#FFFFFF',
                               border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                               zIndex: 999999,
                               position: 'absolute'
                             }}>
                          {menus.map((menu, index) => (
                            <button
                              key={menu.id}
                              onClick={() => {
                                handleMenuChange(menu.id);
                                setIsMenuDropdownOpen(false);
                              }}
                              className={`w-full p-responsive-md transition-colors flex items-center gap-responsive-sm touch-target ${
                                index !== menus.length - 1 ? 'border-b' : ''
                              }`}
                              style={{
                                backgroundColor: selectedMenuId === menu.id ? theme.components?.card?.hover || theme.hover : 'transparent',
                                borderColor: theme.components?.card?.border || '#E0E0E0'
                              }}
                              onMouseEnter={(e) => {
                                if (selectedMenuId !== menu.id) {
                                  e.currentTarget.style.backgroundColor = theme.components?.card?.hover || theme.hover;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (selectedMenuId !== menu.id) {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }
                              }}
                            >
                              <MenuBookIcon className="icon-sm flex-shrink-0" 
                                            style={{ 
                                              color: selectedMenuId === menu.id 
                                                ? theme.primary 
                                                : theme.gray?.[600] || '#666666'
                                            }} />
                              <div className="flex-1 text-left">
                                <div className="font-medium" style={{ color: theme.text }}>
                                  {menu.name}
                                </div>
                                <div className="text-xs flex items-center gap-2" style={{ color: theme.gray?.[600] || '#666666' }}>
                                  <span>{menu.menuType}</span>
                                  <span>•</span>
                                  <span>{menu.itemCount || 0} items</span>
                                  {menu.isDefault && (
                                    <span style={{ color: theme.primary }}>• Default</span>
                                  )}
                                </div>
                              </div>
                              {selectedMenuId === menu.id && (
                                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: theme.primary }}></div>
                              )}
                            </button>
                          ))}
                          <div className="border-t" style={{ borderColor: theme.components?.card?.border || '#E0E0E0' }}>
                            <button
                              onClick={() => {
                                setCreateMenuDialogOpen(true);
                                setIsMenuDropdownOpen(false);
                              }}
                              className="w-full p-responsive-md transition-colors flex items-center gap-responsive-sm touch-target"
                              style={{
                                backgroundColor: 'transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = theme.components?.card?.hover || theme.hover;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <PlusIcon className="icon-sm" style={{ color: theme.primary }} />
                              <div className="font-medium" style={{ color: theme.text }}>
                                Create New Menu
                              </div>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Spacer to push action buttons to the right */}
                    <div className="flex-1"></div>

                    {/* Action Buttons */}
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
                      {/* Dietary filter — mirrors the customer-menu markers */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '8px',
                        padding: '2px',
                        backgroundColor: theme.gray?.[100] || '#F0F0F0',
                      }}>
                        {([['ALL', 'All'], ['VEG', 'Veg'], ['NON_VEG', 'Non-veg'], ['VEGAN', 'Vegan']] as const).map(([value, label]) => (
                          <button
                            key={value}
                            onClick={() => setDietaryFilter(value)}
                            title={label === 'All' ? 'All items' : `Show ${label.toLowerCase()} items`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '6px 10px',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              fontWeight: 500,
                              transition: 'all 0.15s',
                              backgroundColor: dietaryFilter === value ? (theme.components?.card?.background || '#FFFFFF') : 'transparent',
                              color: dietaryFilter === value ? theme.text : (theme.gray?.[500] || '#888'),
                              boxShadow: dietaryFilter === value ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                            }}
                          >
                            {value !== 'ALL' && <DietaryDot type={value} />}
                            <span>{label}</span>
                          </button>
                        ))}
                      </div>

                      {/* View Mode Toggle */}
                                              <div style={{
                             display: 'flex',
                             alignItems: 'center',
                             borderRadius: '8px',
                             padding: '2px',
                             backgroundColor: theme.gray?.[100] || '#F0F0F0',
                           }}>
                        <button
                          onClick={() => setViewMode('list')}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            transition: 'all 0.15s',
                            backgroundColor: viewMode === 'list' ? (theme.components?.card?.background || '#FFFFFF') : 'transparent',
                            color: viewMode === 'list' ? theme.text : (theme.gray?.[500] || '#888'),
                            boxShadow: viewMode === 'list' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                          }}
                        >
                          <Bars4Icon style={{ width: 15, height: 15 }} />
                          <span style={{ marginLeft: 5 }}>List</span>
                        </button>
                        <button
                          onClick={() => setViewMode('grid')}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            transition: 'all 0.15s',
                            backgroundColor: viewMode === 'grid' ? (theme.components?.card?.background || '#FFFFFF') : 'transparent',
                            color: viewMode === 'grid' ? theme.text : (theme.gray?.[500] || '#888'),
                            boxShadow: viewMode === 'grid' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                          }}
                        >
                          <Squares2X2Icon style={{ width: 15, height: 15 }} />
                          <span style={{ marginLeft: 5 }}>Grid</span>
                        </button>
                      </div>
                      
                      {selectedMenu && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <AppIconButton
                            icon={<TimeIcon sx={{ width: 18, height: 18 }} />}
                            tooltip="Configure Time & Days"
                            variant="secondary"
                            size="small"
                            onClick={handleOpenConfig}
                            data-testid="configure-time-days"
                          />
                          <AppIconButton
                            icon={<LocationIcon sx={{ width: 18, height: 18 }} />}
                            tooltip="Assign to Sections"
                            variant="secondary"
                            size="small"
                            onClick={handleOpenAssignSections}
                            data-testid="assign-to-sections"
                          />
                          <AppIconButton
                            icon={<SettingsIcon sx={{ width: 18, height: 18 }} />}
                            tooltip="Manage All Menus"
                            variant="secondary"
                            size="small"
                            onClick={navigateToMenuManagement}
                            data-testid="manage-all-menus"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Bottom Row - Menu Stats (Only visible when menu is selected) */}
                  {selectedMenu && (
                    <div className="mt-3 flex flex-wrap items-center gap-responsive-sm text-sm menu-stats-container desktop-menu-stats tablet-menu-stats" style={{ color: theme.gray?.[600] || '#666666' }}>
                      {(selectedMenu.availableFrom || selectedMenu.availableTo) && (
                        <>
                          <span>•</span>
                          <div className="flex items-center gap-1.5">
                            <TimeIcon className="icon-xs" />
                            <span>{selectedMenu.availableFrom || '00:00'} - {selectedMenu.availableTo || '23:59'}</span>
                          </div>
                        </>
                      )}
                      {selectedMenu.assignedSections && selectedMenu.assignedSections.length > 0 && (
                        <>
                          <span>•</span>
                          <div className="flex items-center gap-1.5">
                            <LocationIcon className="icon-xs flex-shrink-0" />
                            <span 
                              className="font-semibold cursor-pointer hover:opacity-80 transition-opacity"
                              style={{ 
                                color: theme.text,
                                maxWidth: isMobile ? '180px' : '400px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              onClick={(e) => setExpandedSectionsAnchorEl(e.currentTarget)}
                              title="Click to see all sections"
                            >
                              {(() => {
                                const sections = selectedMenu.assignedSections || [];
                                if (sections.length === 0) return '';
                                if (sections.length === 1) {
                                  return `${sections[0].name} (${sections[0].floor})`;
                                }
                                // Mobile: shorter format, Desktop: full format
                                if (isMobile) {
                                  return `${sections[0].name}, +${sections.length - 1} more`;
                                }
                                return `${sections[0].name} (${sections[0].floor}), +${sections.length - 1} more`;
                              })()}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              }
              categories={categories}
              renderItems={(cat) => {
                if (!cat || !cat.id) return null;
                return (
                  <div className="space-y-6 relative">
                    {/* Category header */}
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-responsive-md mb-4 border border-gray-200 dark:border-gray-700">
                      {editingCategoryForms[cat.id] ? (
                        /* Edit Form */
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Category Name
                              </label>
                              <input
                                type="text"
                                value={editingCategoryForms[cat.id].name}
                                onChange={(e) => {
                                  setEditingCategoryForms(prev => ({
                                    ...prev,
                                    [cat.id]: { ...prev[cat.id], name: e.target.value }
                                  }));
                                }}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                                placeholder="Enter category name"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Description
                              </label>
                              <input
                                type="text"
                                value={editingCategoryForms[cat.id].description}
                                onChange={(e) => {
                                  setEditingCategoryForms(prev => ({
                                    ...prev,
                                    [cat.id]: { ...prev[cat.id], description: e.target.value }
                                  }));
                                }}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                                placeholder="Enter description (optional)"
                              />
                            </div>
                          </div>
                          <div className="flex items-center justify-end gap-2">
                                                         <button
                               onClick={async () => {
                                 if (!vendorId || !editingCategoryForms[cat.id]) return;
                                 try {
                                   await api.put(
                                     `/vendors/${vendorId}/menus/${selectedMenuId}/categories/${cat.id}`,
                                     editingCategoryForms[cat.id]
                                   );
                                   setCategories(prev => prev.map(category =>
                                     category.id === cat.id
                                       ? { ...category, ...editingCategoryForms[cat.id] }
                                       : category
                                   ));
                                   setEditingCategoryForms(prev => {
                                     const newForms = { ...prev };
                                     delete newForms[cat.id];
                                     return newForms;
                                   });
                                 } catch (error) {
                                   console.error('Failed to update category:', error);
                                 }
                               }}
                               style={{
                                 backgroundColor: theme.primary,
                                 color: 'var(--text-inverse)'
                               }}
                               onMouseEnter={(e) => {
                                 e.currentTarget.style.filter = 'brightness(1.1)';
                               }}
                               onMouseLeave={(e) => {
                                 e.currentTarget.style.filter = 'none';
                               }}
                               className="inline-flex items-center px-3 py-1.5 text-white rounded-full focus:outline-none transition-colors text-xs font-medium"
                             >
                               Save
                             </button>
                            <button
                              onClick={() => {
                                setEditingCategoryForms(prev => {
                                  const newForms = { ...prev };
                                  delete newForms[cat.id];
                                  return newForms;
                                });
                              }}
                              className="inline-flex items-center px-3 py-1.5 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-colors text-xs font-medium border border-gray-300 dark:border-gray-500"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* Display Mode */
                        <div className="flex justify-between items-center flex-wrap gap-2">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{cat.name}</h2>
                            {cat.description && (
                              <span className="text-sm text-gray-500 dark:text-gray-400 hidden sm:inline">{cat.description}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-responsive-xs">
                            <button
                              onClick={() => {
                                setEditingCategoryForms(prev => ({
                                  ...prev,
                                  [cat.id]: { name: cat.name, description: cat.description || '' }
                                }));
                              }}
                              className="inline-flex items-center justify-center touch-target-sm rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-all"
                              title="Edit Category"
                            >
                              <PencilSquareIcon className="icon-xs" />
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(cat.id)}
                              className="inline-flex items-center justify-center touch-target-sm rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-all"
                              title="Delete Category"
                            >
                              <TrashIcon className="icon-xs" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Items List */}
                    {cat.items.length > 0 ? (
                      <div className={`w-full ${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6' : 'space-y-4'}`}>
                        {cat.items.filter(matchesDietary).map((item: MenuItem) => {
                          const isExpanded = expandedItems.has(item.id)
                          const hasToppings = item.toppings && item.toppings.length > 0
                          return (
                            <div
                              key={item.id}
                              className={`
                                ${viewMode === 'grid'
                                  ? 'bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-700 overflow-hidden group relative h-full flex flex-col'
                                  : 'bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-700 overflow-hidden group'}
                              `}
                            >
                              <div className={`${viewMode === 'grid' ? 'flex flex-col h-full' : ''}`}>
                                {/* Main Content */}
                                <div className={`${viewMode === 'grid' ? 'p-4 md:p-6 flex-1 flex flex-col' : 'p-responsive-md flex items-start gap-responsive-md'}`}>
                                  {/* Image or Emoji (Modern Theme) */}
                                  {isModernTheme ? (
                                    /* Modern Dashboard: Colorful Emoji Circle */
                                    (() => {
                                      const itemStyle = getItemStyle(item.name);
                                      const __isAvail = (item as any).isAvailable ?? (item as any).available ?? true;
                                      return (
                                        <div 
                                          className={`
                                            ${viewMode === 'grid' ? 'w-full h-32 mb-4' : 'flex-shrink-0 w-16 h-16'}
                                            rounded-xl relative flex items-center justify-center
                                          `}
                                          style={{ background: itemStyle.gradient }}
                                        >
                                          <span className={`${viewMode === 'grid' ? 'text-6xl' : 'text-3xl'} drop-shadow-sm`}>
                                            {itemStyle.emoji}
                                          </span>
                                          {!__isAvail && (
                                            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-xl">
                                              <span className="text-white font-medium text-sm">Unavailable</span>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()
                                  ) : (
                                    /* Classic Theme: real image if present, else a warm
                                       category-emoji tile — never the dead grey "Food" box */
                                    (() => {
                                      const __isAvail = (item as any).isAvailable ?? (item as any).available ?? true;
                                      const itemStyle = getItemStyle(item.name);
                                      return (
                                        <div
                                          className={`
                                            ${viewMode === 'grid' ? 'w-full h-48 mb-4' : 'flex-shrink-0 icon-container-md'}
                                            rounded-xl overflow-hidden relative flex items-center justify-center
                                          `}
                                          style={item.imageUrl ? undefined : { background: itemStyle.gradient }}
                                        >
                                          {item.imageUrl ? (
                                            <img
                                              src={item.imageUrl}
                                              alt={item.name}
                                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            />
                                          ) : (
                                            <span className={`${viewMode === 'grid' ? 'text-6xl' : 'text-3xl'} drop-shadow-sm`}>
                                              {itemStyle.emoji}
                                            </span>
                                          )}
                                          {!__isAvail && (
                                            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                              <span className="text-white font-medium text-sm">Unavailable</span>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()
                                  )}

                                  {/* Content */}
                                  <div className={`${viewMode === 'grid' ? 'w-full flex-1 flex flex-col justify-between relative' : 'flex-1 min-w-0'}`}>
                                    <div className={`${viewMode === 'grid' ? 'flex-1' : 'w-full'}`}>
                                      {/* Title with Action Buttons for Grid View - Mobile Responsive */}
                                      <div className="mb-2">
                                        <div className="flex items-start justify-between mb-2">
                                          {(() => {
                                            const itemStyle = isModernTheme ? getItemStyle(item.name) : null;
                                            return (
                                              <h3 
                                                className={`font-semibold transition-colors leading-normal ${
                                                  item.name.length <= 6 
                                                    ? 'text-lg sm:text-xl' 
                                                    : item.name.length <= 10 
                                                    ? 'text-base sm:text-lg' 
                                                    : 'text-sm sm:text-base'
                                                } ${!isModernTheme ? 'text-gray-900 dark:text-gray-100 group-hover:text-purple-600 dark:group-hover:text-purple-400' : ''}`}
                                                style={isModernTheme ? { color: itemStyle?.color } : undefined}
                                              >
                                                <DietaryDot type={item.dietaryType} />
                                                                                                {item.name}
                                              </h3>
                                            );
                                          })()}
                                          
                                          {/* Action Buttons for Grid View - Responsive positioning */}
                                          {viewMode === 'grid' && !(isEditItemModalOpen && editModalData?.itemId === item.id) && (
                                            <div className="flex gap-1 sm:gap-1">
                                              <button
                                                onClick={() => {
                                                  setEditModalData({ categoryId: cat.id, itemId: item.id });
                                                  setEditingItemForms(prev => ({
                                                    ...prev,
                                                    [item.id]: {
                                                      name: item.name,
                                                      price: item.price?.toString() || '',
                                                      available: item.isAvailable,
                                                      quantity: item.quantity?.toString() || '',
                                                      description: item.description || '',
                                                      kitchenId: item.kitchen?.id?.toString() || '',
                                                      spiceLevel: item.spiceLevel || 'NONE',
                                                      allowsSpiceCustomization: item.allowsSpiceCustomization || false,
                                                      imageKey: (item as any).imageKey || undefined,
                                                      imageUrl: item.imageUrl || undefined,
                                                      allergens: item.allergens || [],
                                                      allergenNote: item.allergenNote || '',
                                                      allergenFree: item.allergenFree || false,
                                                      dietaryType: item.dietaryType || 'NONE',
                                                    }
                                                  }));
                                                  setIsEditItemModalOpen(true);
                                                }}
                                                className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded transition-all duration-200"
                                                style={{ backgroundColor: theme.button.secondary.bg, color: theme.button.secondary.text, border: `1px solid ${theme.button.secondary.hover}` }}
                                                title="Edit Item"
                                              >
                                                <PencilSquareIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                              </button>
                                              <button
                                                onClick={() => handleDeleteItem(cat.id, item.id)}
                                                className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-all duration-200"
                                                title="Delete Item"
                                              >
                                                <TrashIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                        
                                        {/* Price Section - Positioned below buttons on mobile, original position on desktop */}
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-xs text-gray-500 dark:text-gray-400">Price</span>
                                          {(() => {
                                            const itemStyle = isModernTheme ? getItemStyle(item.name) : null;
                                            return (
                                              <span 
                                                className={`text-base sm:text-lg font-bold ${!isModernTheme ? 'text-gray-900 dark:text-gray-100' : ''}`}
                                                style={isModernTheme ? { color: itemStyle?.color } : undefined}
                                              >
                                                {CURRENCY.symbol}{item.price != null ? item.price.toFixed(2) : '0.00'}
                                              </span>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                      
                                      {/* Portions Section (inline chips; clickable to expand) */}
                                      {(() => {
                                        const list = portionsByItemId[item.id] || []
                                        const isLoading = loadingPortionsForIds.has(item.id)

                                        // Don't show portions section if no portions and not loading
                                        if (!list.length && !isLoading) {
                                          return null
                                        }

                                        return (
                                          <div className="mb-2">
                                            <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Portions:</span>
                                            <div className="flex flex-wrap gap-1">
                                              {!list.length && isLoading ? (
                                                <span className="text-xs text-gray-400">Loading…</span>
                                              ) : (
                                                <>{(() => {
                                            const maxInline = 3
                                            const inline = list.slice(0, maxInline)
                                            const remaining = list.length - inline.length
                                            return (
                                              <>
                                                {inline.map(p => {
                                                  const itemStyle = isModernTheme ? getItemStyle(item.name) : null;
                                                  return (
                                                    <span
                                                      key={p.id}
                                                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity`}
                                                      style={!isModernTheme ? {
                                                        backgroundColor: p.isDefault ? theme.button.secondary.bg : (theme.gray?.[100] || '#F0F0F0'),
                                                        color: p.isDefault ? theme.button.secondary.text : (theme.gray?.[600] || '#666'),
                                                      } : undefined}
                                                      style={isModernTheme ? {
                                                        backgroundColor: p.isDefault ? itemStyle?.color : `${itemStyle?.color}20`,
                                                        color: p.isDefault ? '#ffffff' : itemStyle?.color,
                                                      } : undefined}
                                                      title={`${p.portionName} — ${CURRENCY.symbol}${(item.price + (p.priceModifier || 0)).toFixed(2)}`}
                                                      onClick={(e) => {
                                                        setExpandedPortionsItemId(item.id)
                                                        setPortionsAnchorEl(e.currentTarget)
                                                      }}
                                                    >
                                                      {p.portionName}
                                                      <span className="ml-1">{CURRENCY.symbol}{(item.price + (p.priceModifier || 0)).toFixed(2)}</span>
                                                    </span>
                                                  );
                                                })}
                                                {remaining > 0 && (
                                                  <span 
                                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors"
                                                    style={{ backgroundColor: theme.button.primary.bg, color: theme.button.primary.text, border: `1px solid ${theme.button.primary.hover}` }}
                                                    onClick={(e) => {
                                                      setExpandedPortionsItemId(item.id)
                                                      setPortionsAnchorEl(e.currentTarget)
                                                    }}
                                                  >
                                                    +{remaining} more
                                                  </span>
                                                )}
                                                </>
                                                )
                                              })()}</>
                                              )}
                                            </div>
                                          </div>
                                        )
                                      })()}
                                      
                                      {/* Description */}
                                      {item.description && (
                                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                                          {item.description}
                                        </p>
                                      )}
                                    </div>
                                    
                                    <div className={`${viewMode === 'grid' ? 'mt-auto pt-4' : 'mt-4'} flex flex-wrap items-center gap-2 status-badges-container`}>
                                      {(() => { const __isAvail = (item as any).isAvailable ?? (item as any).available ?? true; return (
                                        <span className={`status-badge availability-badge inline-flex items-center px-responsive-sm py-responsive-xs rounded-full text-xs font-medium ${
                                          __isAvail 
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' 
                                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                        }`}>
                                          <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                                            __isAvail ? 'bg-green-600' : 'bg-red-600'
                                          }`} />
                                          {__isAvail ? 'Available' : 'Unavailable'}
                                        </span>
                                      ); })()}
                                      {item.quantity != null && (
                                        <span className="status-badge inline-flex items-center px-responsive-sm py-responsive-xs rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                          Stock: {item.quantity}
                                        </span>
                                      )}
                                      {kitchens.length > 0 && (
                                        <div className="flex flex-wrap items-center gap-2 w-full min-w-0">
                                          <div
                                            className={`status-badge kitchen-badge kitchen-trigger lang-pill rounded-full px-responsive-sm py-responsive-xs`}
                                            style={{
                                              color: theme.accent || theme.primary,
                                              backgroundColor: theme.button?.secondary?.bg || theme.background,
                                              border: `1px solid ${theme.button?.secondary?.hover || theme.accent || theme.primary}`,
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '0.25rem',
                                              alignSelf: 'flex-start',
                                              flex: '0 0 auto',
                                              width: 'auto',
                                              maxWidth: 'max-content',
                                              whiteSpace: 'nowrap'
                                            }}
                                          >
                                            <select
                                              value={(Object.prototype.hasOwnProperty.call(pendingKitchenByItemId, item.id)
                                                ? ((pendingKitchenByItemId[item.id] ?? '') as any)
                                                : ((item.kitchenId ?? item.kitchen?.id ?? '') as any))}
                                              onChange={(e) => {
                                                const value = e.target.value
                                                const next = value === '' ? undefined : Number(value)
                                                setPendingKitchenByItemId(prev => ({ ...prev, [item.id]: next }))
                                              }}
                                              className="kitchen-select"
                                              style={{
                                                appearance: 'none',
                                                WebkitAppearance: 'none',
                                                MozAppearance: 'none',
                                                background: 'transparent',
                                                border: 0,
                                                padding: 0,
                                                margin: 0,
                                                font: 'inherit',
                                                color: 'inherit',
                                                width: 'auto',
                                                minWidth: 0,
                                                whiteSpace: 'nowrap',
                                                cursor: 'pointer'
                                              }}
                                            >
                                              <option value="">No Kitchen</option>
                                              {kitchens.map(k => (
                                                <option key={k.id} value={k.id}>{k.name}{k.isDefault ? ' (Default)' : ''}</option>
                                              ))}
                                            </select>
                                            <svg className="shrink-0" width="12" height="12" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.127l3.71-3.896a.75.75 0 111.08 1.04l-4.24 4.454a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                                            </svg>
                                          </div>
                                          {pendingKitchenByItemId.hasOwnProperty(item.id) && (
                                            isMobile ? (
                                              <div className="flex items-center gap-1">
                                                <span
                                                  role="button"
                                                  aria-label="Save"
                                                  tabIndex={0}
                                                  className="inline-flex items-center justify-center select-none"
                                                  style={{
                                                    backgroundColor: 'transparent',
                                                    border: '1px solid var(--border-default)',
                                                    borderRadius: 3,
                                                    padding: 0,
                                                    width: 16,
                                                    height: 16,
                                                    lineHeight: 0,
                                                    boxSizing: 'border-box',
                                                    verticalAlign: 'middle',
                                                    cursor: 'pointer',
                                                  }}
                                                  onKeyDown={async (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); const chosen = pendingKitchenByItemId[item.id]; await updateItemKitchenInline(cat.id, item, chosen); setPendingKitchenByItemId(prev => { const { [item.id]: _, ...rest } = prev; return rest }); const name = chosen ? kitchens.find(k => k.id === chosen)?.name || '' : 'No Kitchen'; const width = Math.ceil((name.length + 2) * 8) + 28; setKitchenLabelWidthByItemId(prev => ({ ...prev, [item.id]: width })); } }}
                                                  onClick={async () => {
                                                    const chosen = pendingKitchenByItemId[item.id]
                                                    await updateItemKitchenInline(cat.id, item, chosen)
                                                    setPendingKitchenByItemId(prev => { const { [item.id]: _, ...rest } = prev; return rest })
                                                    const name = chosen ? kitchens.find(k => k.id === chosen)?.name || '' : 'No Kitchen'
                                                    const width = Math.ceil((name.length + 2) * 8) + 28
                                                    setKitchenLabelWidthByItemId(prev => ({ ...prev, [item.id]: width }))
                                                  }}
                                                >
                                                  <CheckIcon className="w-3 h-3 text-gray-700" />
                                                </span>
                                                <span
                                                  role="button"
                                                  aria-label="Cancel"
                                                  tabIndex={0}
                                                  className="inline-flex items-center justify-center select-none"
                                                  style={{
                                                    backgroundColor: 'transparent',
                                                    border: '1px solid var(--border-default)',
                                                    borderRadius: 3,
                                                    padding: 0,
                                                    width: 16,
                                                    height: 16,
                                                    lineHeight: 0,
                                                    boxSizing: 'border-box',
                                                    verticalAlign: 'middle',
                                                    cursor: 'pointer',
                                                  }}
                                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPendingKitchenByItemId(prev => { const { [item.id]: _, ...rest } = prev; return rest }); } }}
                                                  onClick={() => {
                                                    setPendingKitchenByItemId(prev => { const { [item.id]: _, ...rest } = prev; return rest })
                                                  }}
                                                >
                                                  <XMarkIcon className="w-3 h-3 text-gray-700" />
                                                </span>
                                              </div>
                                            ) : (
                                              <div className="flex flex-wrap items-center gap-2 min-w-0">
                                                <button
                                                  className="inline-flex items-center px-2.5 py-1 text-xs rounded-full transition-colors"
                                                  style={{ color: theme.accent || theme.primary, backgroundColor: theme.button?.secondary?.bg || theme.background, border: `1px solid ${theme.button?.secondary?.hover || theme.accent || theme.primary}` }}
                                                  onClick={async () => {
                                                    const chosen = pendingKitchenByItemId[item.id]
                                                    await updateItemKitchenInline(cat.id, item, chosen)
                                                    setPendingKitchenByItemId(prev => { const { [item.id]: _, ...rest } = prev; return rest })
                                                    const name = chosen ? kitchens.find(k => k.id === chosen)?.name || '' : 'No Kitchen'
                                                    const width = Math.ceil((name.length + 2) * 8) + 28
                                                    setKitchenLabelWidthByItemId(prev => ({ ...prev, [item.id]: width }))
                                                  }}
                                                >
                                                  Save
                                                </button>
                                                <button
                                                  className="inline-flex items-center px-2.5 py-1 text-xs rounded-full transition-colors"
                                                  style={{ color: theme.gray?.[600] || '#555', border: `1px solid ${theme.gray?.[300] || '#D1D5DB'}` }}
                                                  onClick={() => {
                                                    setPendingKitchenByItemId(prev => { const { [item.id]: _, ...rest } = prev; return rest })
                                                  }}
                                                >
                                                  Cancel
                                                </button>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      )}
                                      {hasToppings && viewMode === 'grid' && (
                                        <button
                                          onClick={() => {
                                            setExpandedItems(prev => {
                                              const newSet = new Set(prev);
                                              if (newSet.has(item.id)) {
                                                newSet.delete(item.id);
                                              } else {
                                                newSet.add(item.id);
                                              }
                                              return newSet;
                                            });
                                          }}
                                          className="inline-flex items-center gap-1 px-responsive-sm py-responsive-xs rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                        >
                                          <span>{item.toppings?.length} Toppings</span>
                                          {isExpanded ? (
                                            <ChevronUpIcon className="icon-xs" />
                                          ) : (
                                            <ChevronDownIcon className="icon-xs" />
                                          )}
                                        </button>
                                      )}
                                    </div>
                                    
                                    {/* Show toppings inline in list view */}
                                    {hasToppings && viewMode === 'list' && (
                                      <div className="mt-3">
                                        <div className="flex flex-wrap gap-1">
                                          {item.toppings?.map((topping) => (
                                            <span
                                              key={topping.id}
                                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                                            >
                                              {topping.name}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {/* Actions - Only for List View - Positioned to the far right */}
                                  {viewMode === 'list' && (
                                    <div className="flex gap-1 flex-shrink-0">
                                      <button
                                        onClick={() => {
                                          setEditModalData({ categoryId: cat.id, itemId: item.id });
                                          setEditingItemForms(prev => ({
                                            ...prev,
                                            [item.id]: {
                                              name: item.name,
                                              price: item.price?.toString() || '',
                                              available: item.isAvailable,
                                              quantity: item.quantity?.toString() || '',
                                              description: item.description || '',
                                              kitchenId: item.kitchen?.id?.toString() || '',
                                              spiceLevel: item.spiceLevel || 'NONE',
                                              allowsSpiceCustomization: item.allowsSpiceCustomization || false,
                                              allergens: item.allergens || [],
                                              allergenNote: item.allergenNote || '',
                                              allergenFree: item.allergenFree || false,
                                              dietaryType: item.dietaryType || 'NONE'
                                            }
                                          }));
                                          setIsEditItemModalOpen(true);
                                        }}
                                        className="inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-all duration-200"
                                        title="Edit Item"
                                      >
                                        <PencilSquareIcon className="w-3 h-3" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteItem(cat.id, item.id)}
                                        className="inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-all duration-200"
                                        title="Delete Item"
                                      >
                                        <TrashIcon className="w-3 h-3" />
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {/* Expandable Toppings Section */}
                                {hasToppings && isExpanded && (
                                  <div className="p-responsive-md pt-0 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                                    <div className="mt-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                          Available Toppings ({item.toppings?.length})
                                        </h4>
                                        <div className="flex items-center gap-responsive-xs">
                                          <input
                                            type="text"
                                            value={inlineToppingInputs[item.id] || ''}
                                            onChange={(e) => setInlineToppingInputs(prev => ({ ...prev, [item.id]: e.target.value }))}
                                            onKeyPress={(e) => {
                                              if (e.key === 'Enter') {
                                                handleAddTopping(cat.id, item.id);
                                              }
                                            }}
                                            placeholder="New topping name"
                                            className="px-responsive-sm py-responsive-xs text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                          />
                                          <button
                                            onClick={() => handleAddTopping(cat.id, item.id)}
                                            disabled={!inlineToppingInputs[item.id]?.trim()}
                                            className={`inline-flex items-center gap-1 px-responsive-sm py-responsive-xs text-xs font-medium text-white rounded-lg transition-colors btn-size-responsive ${
                                              !inlineToppingInputs[item.id]?.trim() 
                                                ? 'bg-gray-300 dark:bg-gray-600' 
                                                : 'bg-gray-700 hover:bg-gray-800 dark:bg-gray-600 dark:hover:bg-gray-700'
                                            }`}
                                          >
                                            <PlusIcon className="icon-xs" />
                                            Add
                                          </button>
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-1 gap-2">
                                        {item.toppings?.map((topping) => (
                                          <div
                                            key={topping.id}
                                            className="flex items-center justify-between p-responsive-sm bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                                          >
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                              {topping.name}
                                            </span>
                                            <button
                                              onClick={() => handleDeleteTopping(cat.id, item.id, topping.id)}
                                              className="text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 p-1 rounded transition-colors"
                                            >
                                              <TrashIcon className="icon-xs" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-16 px-8 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-700">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                          <RestaurantMenuIcon className="icon-lg text-gray-400 dark:text-gray-500" />
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 text-lg font-medium mb-2">No items in this category yet</p>
                        <p className="text-gray-400 dark:text-gray-500 text-sm">Click "Add Item" in the sidebar to add your first item</p>
                      </div>
                    )}
                  </div>
                );
              }}
              renderSidebarItem={(category, isSelected) => (
                <CategoryListItem
                  category={category}
                  isSelected={isSelected}
                  isEditing={!!editingCategoryForms[category.id]}
                  editForm={editingCategoryForms[category.id]}
                  onEdit={() => {
                    setEditingCategoryForms(prev => ({
                      ...prev,
                      [category.id]: { name: category.name, description: category.description || '' }
                    }));
                  }}
                  onSave={async () => {
                    if (!vendorId || !editingCategoryForms[category.id] || !selectedMenuId) return;
                    try {
                      await api.put(
                        `/vendors/${vendorId}/menus/${selectedMenuId}/categories/${category.id}`,
                        editingCategoryForms[category.id]
                      );
                      setCategories(prev => prev.map(cat =>
                        cat.id === category.id
                          ? { ...cat, ...editingCategoryForms[category.id] }
                          : cat
                      ));
                      setEditingCategoryForms(prev => {
                        const newForms = { ...prev };
                        delete newForms[category.id];
                        return newForms;
                      });
                    } catch (error) {
                      console.error('Failed to update category:', error);
                    }
                  }}
                  onCancel={() => {
                    setEditingCategoryForms(prev => {
                      const newForms = { ...prev };
                      delete newForms[category.id];
                      return newForms;
                    });
                  }}
                  onChange={(field, value) => {
                    setEditingCategoryForms(prev => ({
                      ...prev,
                      [category.id]: { ...prev[category.id], [field]: value }
                    }));
                  }}
                  onDelete={() => handleDeleteCategory(category.id)}
                  onAddItem={() => handleAddItem(category.id)}
                />
              )}
              vendorId={vendorId ? parseInt(vendorId) : undefined}
              onAddCategory={async (categoryData: { name: string; description: string; imageKey?: string }) => {
                if (!vendorId || !selectedMenuId) return;
                try {
                  const response = await api.post(
                    `/api/menu-management/menus/${selectedMenuId}/categories`,
                    categoryData
                  );
                  setCategories(prev => {
                    const next = [...prev, { ...response.data, items: [] }];
                    // Ask CardLayoutTheme to focus newly created category
                    try {
                      (globalThis as any).__cardLayoutCategoryMemory__ = (globalThis as any).__cardLayoutCategoryMemory__ || {};
                      const key = typeof window !== 'undefined' ? window.location.pathname : 'default';
                      (globalThis as any).__cardLayoutCategoryMemory__[key] = response.data.id;
                    } catch {}
                    return next;
                  });
                } catch (error) {
                  console.error('Failed to add category:', error);
                }
              }}
              onAddItemClick={handleAddItem}
            />

            {/* Add Item Modal */}
            <Modal
              isOpen={isAddItemModalOpen}
              onClose={() => {
                setIsAddItemModalOpen(false);
                setCurrentCategoryId(null);
              }}
              title="Add New Item"
            >
              <AddItemForm
                vendorId={vendorId ? parseInt(vendorId) : undefined}
                categoryId={currentCategoryId || undefined}
                onSubmit={handleAddItemSubmit}
                onCancel={() => {
                  setIsAddItemModalOpen(false);
                  setCurrentCategoryId(null);
                }}
              />
            </Modal>

            {/* Edit Item Modal */}
            <Modal
              isOpen={isEditItemModalOpen}
              onClose={() => {
                setIsEditItemModalOpen(false);
                setEditModalData(null);
              }}
              title="Edit Item"
            >
              {editModalData && editingItemForms[editModalData.itemId] && (
                <EditItemForm
                  initialData={{
                    ...editingItemForms[editModalData.itemId],
                    // Defensive: ensure available reflects either field on the item
                    available: ((categories.find(c=>c.id===editModalData.categoryId)?.items.find(i=>i.id===editModalData.itemId) as any)?.isAvailable ?? (categories.find(c=>c.id===editModalData.categoryId)?.items.find(i=>i.id===editModalData.itemId) as any)?.available ?? true),
                    id: editModalData.itemId
                  }}
                  vendorId={vendorId ? parseInt(vendorId) : undefined}
                  onSubmit={handleEditItemSubmit}
                  onCancel={() => {
                    setIsEditItemModalOpen(false);
                    setEditModalData(null);
                  }}
                  onManagePortions={() => {
                    const category = categories.find(cat => cat.id === editModalData.categoryId);
                    const item = category?.items?.find(item => item.id === editModalData.itemId);
                    if (item) {
                      setPortionsModalData({
                        menuItemId: item.id,
                        menuItemName: item.name,
                        menuItemPrice: item.price
                      });
                      setIsEditItemModalOpen(false);
                    }
                  }}
                />
              )}
            </Modal>

            {/* Configure Time & Days Dialog */}
            <Dialog 
              open={configDialogOpen} 
              onClose={() => setConfigDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>
                <Box display="flex" alignItems="center" gap={2}>
                  <TimeIcon className="icon-sm" />
                  Configure Availability
                </Box>
              </DialogTitle>
              <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TimePicker
                      label="Available From"
                      value={menuConfig.availableFrom ? new Date(`2000-01-01T${menuConfig.availableFrom}`) : null}
                      onChange={(time) => {
                        if (time) {
                          const hours = time.getHours().toString().padStart(2, '0');
                          const minutes = time.getMinutes().toString().padStart(2, '0');
                          setMenuConfig({ ...menuConfig, availableFrom: `${hours}:${minutes}` });
                        }
                      }}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true,
                          placeholder: "hh:mm aa",
                          sx: {
                            '& .MuiInputBase-input': {
                              textAlign: 'center',
                              padding: '8px 10px',
                              height: '36px',
                              fontSize: '14px',
                              '&::placeholder': {
                                opacity: 0.7,
                                fontSize: '14px',
                                letterSpacing: '0.5px'
                              }
                            },
                            '& .MuiInputAdornment-root .MuiIconButton-root': {
                              backgroundColor: 'transparent !important',
                              color: (theme.gray?.[600] || '#4b5563') + ' !important',
                              borderRadius: '8px',
                              '&:hover': {
                                backgroundColor: (theme.gray?.[100] || '#f3f4f6') + ' !important'
                              }
                            }
                          }
                        },
                        popper: {
                          sx: { 
                            zIndex: 100002,
                            marginTop: '4px',
                            '& .MuiPickersPopper-root': {
                              zIndex: '100002 !important',
                              transform: 'translateX(-50%)',
                              left: '50% !important'
                            },
                            '& .MuiButton-root': {
                              margin: '0 4px',
                              padding: '4px 10px'
                            }
                          }
                        },
                        actionBar: {
                          actions: ['cancel', 'accept'],
                          sx: {
                            '& .MuiButton-root': {
                              color: (theme.gray?.[600] || '#555') + ' !important',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: (theme.gray?.[100] || '#F0F0F0') + ' !important'
                              },
                              '&:last-child': {
                                backgroundColor: theme.primary + ' !important',
                                color: 'var(--text-inverse) !important',
                                '&:hover': {
                                  filter: 'brightness(1.1)'
                                }
                              }
                            }
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TimePicker
                      label="Available To"
                      value={menuConfig.availableTo ? new Date(`2000-01-01T${menuConfig.availableTo}`) : null}
                      onChange={(time) => {
                        if (time) {
                          const hours = time.getHours().toString().padStart(2, '0');
                          const minutes = time.getMinutes().toString().padStart(2, '0');
                          setMenuConfig({ ...menuConfig, availableTo: `${hours}:${minutes}` });
                        }
                      }}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true,
                          placeholder: "hh:mm aa",
                          sx: {
                            '& .MuiInputBase-input': {
                              textAlign: 'center',
                              padding: '8px 10px',
                              height: '36px',
                              fontSize: '14px',
                              '&::placeholder': {
                                opacity: 0.7,
                                fontSize: '14px',
                                letterSpacing: '0.5px'
                              }
                            },
                            '& .MuiInputAdornment-root .MuiIconButton-root': {
                              backgroundColor: 'transparent !important',
                              color: (theme.gray?.[600] || '#4b5563') + ' !important',
                              borderRadius: '8px',
                              '&:hover': {
                                backgroundColor: (theme.gray?.[100] || '#f3f4f6') + ' !important'
                              }
                            }
                          }
                        },
                        popper: {
                          sx: { 
                            zIndex: 100002,
                            marginTop: '4px',
                            '& .MuiPickersPopper-root': {
                              zIndex: '100002 !important',
                              transform: 'translateX(-50%)',
                              left: '50% !important'
                            },
                            '& .MuiButton-root': {
                              margin: '0 4px',
                              padding: '4px 10px'
                            }
                          }
                        },
                        actionBar: {
                          actions: ['cancel', 'accept'],
                          sx: {
                            '& .MuiButton-root': {
                              color: (theme.gray?.[600] || '#555') + ' !important',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: (theme.gray?.[100] || '#F0F0F0') + ' !important'
                              },
                              '&:last-child': {
                                backgroundColor: theme.primary + ' !important',
                                color: 'var(--text-inverse) !important',
                                '&:hover': {
                                  filter: 'brightness(1.1)'
                                }
                              }
                            }
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Available Days
                    </Typography>
                    <FormGroup row>
                      {WEEKDAYS.map((day) => (
                        <FormControlLabel
                          key={day.value}
                          control={
                            <Checkbox
                              checked={menuConfig.availableDays?.includes(day.value) || false}
                              onChange={(e) => {
                                // Ensure days is always an array
                                let currentDays: string[] = [];
                                if (menuConfig.availableDays) {
                                  if (typeof menuConfig.availableDays === 'string') {
                                    currentDays = menuConfig.availableDays.split(',').map(d => d.trim()).filter(d => d);
                                  } else if (Array.isArray(menuConfig.availableDays)) {
                                    currentDays = menuConfig.availableDays;
                                  }
                                }
                                
                                if (e.target.checked) {
                                  setMenuConfig({ ...menuConfig, availableDays: [...currentDays, day.value] });
                                } else {
                                  setMenuConfig({ ...menuConfig, availableDays: currentDays.filter(d => d !== day.value) });
                                }
                              }}
                            />
                          }
                          label={day.label}
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button 
                  onClick={() => setConfigDialogOpen(false)}
                  sx={{
                    backgroundColor: 'transparent !important',
                    color: (theme.gray?.[700] || '#374151') + ' !important',
                    border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
                    fontWeight: 500,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: (theme.gray?.[100] || '#f3f4f6') + ' !important',
                      borderColor: (theme.gray?.[400] || '#9ca3af') + ' !important'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveConfig}
                  variant="contained"
                  sx={{
                    backgroundColor: theme.primary + ' !important',
                    color: 'var(--text-inverse) !important',
                    border: 'none !important',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 'none !important',
                    '&:hover': {
                      backgroundColor: (theme.primaryHover || theme.primary) + ' !important',
                      filter: 'brightness(1.1)',
                      boxShadow: 'none !important'
                    }
                  }}
                >
                  Save Configuration
                </Button>
              </DialogActions>
            </Dialog>

            {/* Assign Sections Dialog */}
            <Dialog
              open={assignSectionsDialogOpen}
              onClose={() => {
                setAssignSectionsDialogOpen(false)
                setSelectedSections([]) // Clear selected sections on dialog close
                setSectionsToReassign([]) // Clear reassignment tracking
              }}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>
                <Box display="flex" alignItems="center" gap={2}>
                  <LocationIcon className="icon-sm" />
                  Assign Menu to Sections
                </Box>
              </DialogTitle>
              <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select the sections where "{selectedMenu?.name}" should be available
                </Typography>
                <List sx={{ width: '100%' }}>
                  {/* Takeaway Orders - Special entry at top */}
                  <ListItem
                    sx={{
                      backgroundColor: theme.button?.secondary?.bg || theme.background,
                      borderRadius: 1,
                      mb: 1,
                      border: `1px solid ${theme.button?.secondary?.hover || theme.primary}`
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <TakeawayIcon sx={{ color: theme.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600, color: theme.text }}>
                          Takeaway Orders
                        </Typography>
                      }
                      secondary="Use this menu for all takeaway/pickup orders"
                    />
                    <ListItemSecondaryAction>
                      <Checkbox
                        edge="end"
                        checked={selectedSections.includes(TAKEAWAY_SECTION_ID)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSections([...selectedSections, TAKEAWAY_SECTION_ID]);
                          } else {
                            setSelectedSections(selectedSections.filter(id => id !== TAKEAWAY_SECTION_ID));
                          }
                        }}
                        sx={{
                          color: theme.gray?.[600] || '#555',
                          '&.Mui-checked': {
                            color: theme.primary,
                          }
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>

                  <Divider sx={{ my: 2 }}>
                    <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#6B7280' }}>
                      Dine-in Sections
                    </Typography>
                  </Divider>

                  {Object.entries(groupSectionsByFloor()).map(([floorName, floorSections]) => {
                    const selectedInFloor = floorSections.filter(s => selectedSections.includes(s.id)).length;
                    const floorNumber = floorSections[0]?.floorNumber;
                    return (
                      <Box key={floorName} sx={{ mb: 2 }}>
                        <ListItem 
                          sx={{ 
                            backgroundColor: 'rgba(55, 65, 81, 0.05)',
                            borderRadius: 1,
                            mb: 0.5,
                            py: 0.5
                          }}
                        >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <svg className="icon-sm text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </ListItemIcon>
                        <ListItemText 
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {floorNumber !== undefined ? `Floor ${floorNumber}: ${floorName}` : floorName}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {selectedInFloor > 0 && (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: theme.gray?.[600] || '#555',
                                      fontWeight: 600,
                                      backgroundColor: 'rgba(55, 65, 81, 0.1)',
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: 0.5
                                    }}
                                  >
                                    {selectedInFloor} selected
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  {floorSections.length} section{floorSections.length !== 1 ? 's' : ''}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      <Box sx={{ pl: 4 }}>
                        {floorSections.map((section) => {
                          const assignment = getSectionAssignment(section.id);
                          const isAvailable = isSectionAvailable(section.id, selectedMenuId || undefined);
                          const isChecked = selectedSections.includes(Number(section.id));

                          return (
                            <ListItem 
                              key={section.id}
                              sx={{ 
                                py: 1,
                                opacity: isAvailable ? 1 : 0.6,
                                backgroundColor: assignment && !isAvailable ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                                '&:hover': { 
                                  backgroundColor: isAvailable ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.02)' 
                                }
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                {assignment && !isAvailable ? (
                                  <svg className="icon-xs text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                ) : assignment?.isDefault ? (
                                  <svg className="icon-xs text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                  </svg>
                                ) : (
                                  <svg className="icon-xs text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14v6m-3-3h6M6 10h2a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2zm10 0h2a2 2 0 002-2V6a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2zM6 20h2a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2z" />
                                  </svg>
                                )}
                              </ListItemIcon>
                              <ListItemText 
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: isChecked ? 600 : 400 }}>
                                      {section.name}
                                    </Typography>
                                    {assignment && !isAvailable && (
                                      <Typography 
                                        variant="caption" 
                                        sx={{ 
                                          color: theme.danger || '#dc2626',
                                          backgroundColor: `${theme.danger || '#dc2626'}15`,
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: 0.5,
                                          fontSize: '0.7rem',
                                          fontWeight: 500
                                        }}
                                      >
                                        {assignment.menuName}
                                      </Typography>
                                    )}
                                    {assignment?.isDefault && isAvailable && (
                                      <Typography 
                                        variant="caption" 
                                        sx={{ 
                                          color: theme.button.primary.text,
                                          backgroundColor: theme.button.primary.bg,
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: 0.5,
                                          fontSize: '0.7rem',
                                          fontWeight: 500
                                        }}
                                      >
                                        Default
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                              <ListItemSecondaryAction>
                                <Checkbox
                                  edge="end"
                                  checked={isChecked}
                                  disabled={!isAvailable}
                                  onChange={(e) => handleSectionToggle(Number(section.id), e.target.checked)}
                                  sx={{
                                    color: isAvailable ? (theme.gray?.[600] || '#555') : (theme.gray?.[300] || '#ccc'),
                                    '&.Mui-checked': {
                                      color: theme.primary,
                                    },
                                    '&.Mui-disabled': {
                                      color: theme.gray?.[300] || '#ccc',
                                    }
                                  }}
                                />
                              </ListItemSecondaryAction>
                            </ListItem>
                          );
                        })}
                      </Box>
                    </Box>
                    );
                  })}
                </List>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setAssignSectionsDialogOpen(false)
                    setSelectedSections([]) // Clear selected sections on cancel
                    setSectionsToReassign([]) // Clear reassignment tracking
                  }}
                  sx={{
                    backgroundColor: 'transparent !important',
                    color: (theme.gray?.[700] || '#374151') + ' !important',
                    border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
                    fontWeight: 500,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: (theme.gray?.[100] || '#f3f4f6') + ' !important',
                      borderColor: (theme.gray?.[400] || '#9ca3af') + ' !important'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAssignSections}
                  variant="contained"
                  sx={{
                    backgroundColor: theme.primary + ' !important',
                    color: 'var(--text-inverse) !important',
                    border: 'none !important',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 'none !important',
                    '&:hover': {
                      backgroundColor: (theme.primaryHover || theme.primary) + ' !important',
                      filter: 'brightness(1.1)',
                      boxShadow: 'none !important'
                    }
                  }}
                >
                  Assign Sections
                </Button>
              </DialogActions>
            </Dialog>

            {/* Portions Manager Modal */}
            {portionsModalData && (
              <MenuItemPortionsManager
                open={!!portionsModalData}
                onClose={() => setPortionsModalData(null)}
                menuItemId={portionsModalData.menuItemId}
                menuItemName={portionsModalData.menuItemName}
                menuItemPrice={portionsModalData.menuItemPrice}
                onPortionsUpdated={() => {
                  // Refresh the menu data if needed
                  setRefreshTrigger(prev => prev + 1); // Trigger re-fetch
                }}
              />
            )}

            {/* Portions Expanded View - Desktop: Popover, Mobile: Bottom Sheet */}
            {expandedPortionsItemId && (() => {
              const expandedItem = categories
                .flatMap(cat => cat.items || [])
                .find(it => it.id === expandedPortionsItemId)
              const expandedPortions = portionsByItemId[expandedPortionsItemId] || []
              
              if (!expandedItem) return null

              const handleClose = () => {
                setExpandedPortionsItemId(null)
                setPortionsAnchorEl(null)
              }

              const content = (
                <Box sx={{ p: 2, minWidth: isMobile ? '100%' : 280, maxWidth: isMobile ? '100%' : 400 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {expandedItem.name}
                    </Typography>
                    {isMobile && (
                      <AppIconButton
                        icon={<CloseIcon fontSize="small" />}
                        tooltip="Close"
                        variant="ghost"
                        size="small"
                        onClick={handleClose}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Base Price: {CURRENCY.symbol}{expandedItem.price?.toFixed(2) || '0.00'}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {expandedPortions.map(p => (
                      <Box
                        key={p.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: p.isDefault ? theme.button.secondary.bg : 'rgba(0, 0, 0, 0.02)',
                          border: '1px solid',
                          borderColor: p.isDefault ? theme.button.secondary.hover : 'rgba(0, 0, 0, 0.06)',
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={p.isDefault ? 600 : 400}>
                            {p.portionName}
                            {p.isDefault && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  ml: 1,
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 0.5,
                                  backgroundColor: theme.button.primary.bg,
                                  color: theme.button.primary.text,
                                  fontWeight: 600,
                                }}
                              >
                                DEFAULT
                              </Typography>
                            )}
                          </Typography>
                          {p.priceModifier !== 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {p.priceModifier > 0 ? '+' : ''}{CURRENCY.symbol}{p.priceModifier.toFixed(2)} modifier
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="body1" fontWeight={600} color="primary">
                          {CURRENCY.symbol}{(expandedItem.price + (p.priceModifier || 0)).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={() => {
                      setPortionsModalData({
                        menuItemId: expandedItem.id,
                        menuItemName: expandedItem.name,
                        menuItemPrice: expandedItem.price
                      })
                      handleClose()
                    }}
                    sx={{ mt: 2 }}
                  >
                    Manage Portions
                  </Button>
                </Box>
              )

              if (isMobile) {
                return (
                  <Drawer
                    anchor="bottom"
                    open={!!expandedPortionsItemId}
                    onClose={handleClose}
                    PaperProps={{
                      sx: {
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        maxHeight: '80vh',
                      }
                    }}
                  >
                    {content}
                  </Drawer>
                )
              }

              return (
                <Popover
                  open={!!portionsAnchorEl}
                  anchorEl={portionsAnchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    sx: {
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      borderRadius: 2,
                    }
                  }}
                >
                  {content}
                </Popover>
              )
            })()}

            {/* Sections Expanded View - Desktop: Popover, Mobile: Bottom Sheet */}
            {expandedSectionsAnchorEl && selectedMenu && selectedMenu.assignedSections && (() => {
              const sections = selectedMenu.assignedSections || [];
              
              const handleClose = () => {
                setExpandedSectionsAnchorEl(null);
              };

              const content = (
                <Box sx={{ p: 2, minWidth: isMobile ? '100%' : 300, maxWidth: isMobile ? '100%' : 500 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Assigned Sections ({sections.length})
                    </Typography>
                    {isMobile && (
                      <AppIconButton
                        icon={<CloseIcon fontSize="small" />}
                        tooltip="Close"
                        variant="ghost"
                        size="small"
                        onClick={handleClose}
                      />
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {sections.map(section => (
                      <Box
                        key={section.id}
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          border: '1px solid',
                          borderColor: 'rgba(0, 0, 0, 0.06)',
                        }}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          {section.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {section.floor}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );

              if (isMobile) {
                return (
                  <Drawer
                    anchor="bottom"
                    open={!!expandedSectionsAnchorEl}
                    onClose={handleClose}
                    PaperProps={{
                      sx: {
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        maxHeight: '70vh',
                      }
                    }}
                  >
                    {content}
                  </Drawer>
                );
              }

              return (
                <Popover
                  open={!!expandedSectionsAnchorEl}
                  anchorEl={expandedSectionsAnchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    sx: {
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      borderRadius: 2,
                    }
                  }}
                >
                  {content}
                </Popover>
              );
            })()}
          </>
        )}

        {/* Create Menu Dialog — rendered outside the menus-exist branch so the
            "Create First Menu" empty-state button can open it too */}
        <Dialog
          open={createMenuDialogOpen}
          onClose={() => setCreateMenuDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={2}>
              <MenuBookIcon className="icon-sm" />
              Create New Menu
            </Box>
          </DialogTitle>
          <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Menu Name"
                  value={newMenuForm.name}
                  onChange={(e) => setNewMenuForm({ ...newMenuForm, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={newMenuForm.description}
                  onChange={(e) => setNewMenuForm({ ...newMenuForm, description: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Menu Type</InputLabel>
                  <Select
                    value={newMenuForm.menuType}
                    onChange={(e) => setNewMenuForm({ ...newMenuForm, menuType: e.target.value })}
                    label="Menu Type"
                  >
                    <MuiMenuItem value="REGULAR">Regular</MuiMenuItem>
                    <MuiMenuItem value="BREAKFAST">Breakfast</MuiMenuItem>
                    <MuiMenuItem value="LUNCH">Lunch</MuiMenuItem>
                    <MuiMenuItem value="DINNER">Dinner</MuiMenuItem>
                    <MuiMenuItem value="DRINKS">Drinks</MuiMenuItem>
                    <MuiMenuItem value="SPECIAL">Special</MuiMenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newMenuForm.isActive}
                      onChange={(e) => setNewMenuForm({ ...newMenuForm, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newMenuForm.isDefault}
                      onChange={(e) => setNewMenuForm({ ...newMenuForm, isDefault: e.target.checked })}
                    />
                  }
                  label="Default Menu"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setCreateMenuDialogOpen(false)}
              sx={{
                color: theme.gray?.[600] || '#555',
                '&:hover': {
                  backgroundColor: `${theme.gray?.[100] || '#F0F0F0'}`
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateMenu}
              variant="contained"
              disabled={!newMenuForm.name.trim()}
              sx={{
                backgroundColor: theme.primary,
                color: 'var(--text-inverse)',
                '&:hover': {
                  backgroundColor: theme.primaryHover || theme.primary,
                },
                '&:disabled': {
                  backgroundColor: theme.gray?.[200] || '#e5e7eb',
                  color: theme.gray?.[500] || '#9ca3af'
                }
              }}
            >
              Create Menu
            </Button>
          </DialogActions>
        </Dialog>
      </>
    </LocalizationProvider>
  );
}

export default AdminVendorMenu;